import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import {
  Search,
  RefreshCw,
  FileArchive,
  CheckSquare,
  Square,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Loader2,
  History as HistoryIcon,
  ChevronLeft,
  ChevronRight,
  Database,
} from 'lucide-react';

const MONTHS = [
  { v: 1, label: '01 - January' }, { v: 2, label: '02 - February' },
  { v: 3, label: '03 - March' }, { v: 4, label: '04 - April' },
  { v: 5, label: '05 - May' }, { v: 6, label: '06 - June' },
  { v: 7, label: '07 - July' }, { v: 8, label: '08 - August' },
  { v: 9, label: '09 - September' }, { v: 10, label: '10 - October' },
  { v: 11, label: '11 - November' }, { v: 12, label: '12 - December' },
];
const MONTH_LABEL = (m) => (MONTHS.find((x) => x.v === Number(m))?.label || m || '—');

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

/**
 * A select with a type-to-filter box. Used for Year and Month, which are long
 * lists (21 years) where scrolling to 2013 is slower than typing "13".
 * Options: [{ value, label, hint }] — hint shows greyed on the right.
 */
const FilterableSelect = ({ label, value, options, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const boxRef = useRef(null);

  const current = options.find((o) => String(o.value) === String(value));
  // Match on the label only. The hint carries counts like "1,234 zips", and
  // including it would make typing a digit match unrelated rows.
  const matches = options.filter(
    (o) => filter === '' || String(o.label).toLowerCase().includes(filter.trim().toLowerCase()),
  );

  // Close when clicking away or pressing Escape, so it behaves like a native select.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (v) => { onChange(v); setOpen(false); setFilter(''); };

  return (
    <div ref={boxRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => { setOpen((s) => !s); setFilter(''); }}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-left flex items-center justify-between focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
      >
        <span className="truncate">{current ? current.label : <span className="text-gray-400">Select…</span>}</span>
        <svg className="w-4 h-4 text-gray-500 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
          <div className="p-2 sticky top-0 bg-white border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              // Enter picks the only remaining match — type "jul", press Enter.
              onKeyDown={(e) => { if (e.key === 'Enter' && matches.length > 0) { e.preventDefault(); pick(matches[0].value); } }}
              placeholder={placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="divide-y divide-gray-100">
            {matches.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => pick(o.value)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center justify-between gap-2 ${String(o.value) === String(value) ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
              >
                <span>{o.label}</span>
                {o.hint && <span className="text-xs text-gray-400 flex-shrink-0">{o.hint}</span>}
              </button>
            ))}
            {matches.length === 0 && (
              <div className="px-3 py-3 text-sm text-gray-500">No match.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RepeatOrders = () => {
  const { token } = useAuth();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const currentYear = new Date().getFullYear();
  const YEARS = [];
  for (let y = currentYear; y >= 2006; y -= 1) YEARS.push(y);

  const [tab, setTab] = useState('find'); // 'find' | 'history'

  // ── Find/Extract state ──────────────────────────────────────────────
  const [activeJob, setActiveJob] = useState('');
  const [oldJob, setOldJob] = useState('');
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  const [finding, setFinding] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [proceeding, setProceeding] = useState(false);
  const [done, setDone] = useState(null);

  const resetOutput = () => { setResult(null); setSelected({}); setDone(null); setError(''); };

  const handleFind = async (e) => {
    e.preventDefault();
    if (!activeJob.trim()) { setError('Please enter the active job number.'); return; }
    if (!oldJob.trim()) { setError('Please enter the old job number.'); return; }
    setFinding(true);
    resetOutput();
    try {
      const res = await api.post('/repeat-orders/find', {
        active_job: activeJob.trim(),
        old_job_number: oldJob.trim(),
        year: Number(year),
        month: Number(month),
      }, authHeader);
      if (!res.data?.found) {
        setError(res.data?.message || 'No matching archive ZIP was found.');
      } else {
        setResult(res.data);
        const initial = {};
        (res.data.files || []).forEach((f) => { initial[f.path] = true; });
        setSelected(initial);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Search failed. Please try again.');
    } finally {
      setFinding(false);
    }
  };

  const toggle = (path) => setSelected((s) => ({ ...s, [path]: !s[path] }));
  const allSelected = result && (result.files || []).length > 0
    && (result.files || []).every((f) => selected[f.path]);
  const toggleAll = () => {
    if (!result) return;
    const next = {};
    (result.files || []).forEach((f) => { next[f.path] = !allSelected; });
    setSelected(next);
  };
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const handleProceed = async () => {
    if (!result) return;
    const keep = (result.files || []).map((f) => f.path).filter((p) => selected[p]);
    const deleteCount = (result.files || []).length - keep.length;
    const msg = deleteCount === 0
      ? 'Keep all files? Nothing will be deleted. Continue?'
      : `This will permanently delete ${deleteCount} file(s) and keep ${keep.length}. Continue?`;
    if (!window.confirm(msg)) return;
    setProceeding(true);
    setError('');
    try {
      const res = await api.post('/repeat-orders/proceed', {
        extract_id: result.extract_id,
        keep,
        active_job: activeJob.trim(),
        old_job_number: oldJob.trim(),
        year: Number(year),
        month: Number(month),
        zip_name: result.zip_name,
      }, authHeader);
      setDone({ kept: res.data?.kept || keep, deleted_count: res.data?.deleted_count ?? deleteCount, folder: res.data?.folder });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply selection.');
    } finally {
      setProceeding(false);
    }
  };

  // ── History state ───────────────────────────────────────────────────
  const [hist, setHist] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [histLoading, setHistLoading] = useState(false);
  const [histQuery, setHistQuery] = useState('');
  const [histSearch, setHistSearch] = useState('');
  const [histPage, setHistPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const PAGE_SIZE = 15;

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const res = await api.get('/repeat-orders/history', {
        ...authHeader,
        params: { page: histPage, page_size: PAGE_SIZE, q: histSearch || undefined },
      });
      setHist(res.data || { items: [], total: 0, page: 1, pages: 1 });
    } catch (err) {
      setHist({ items: [], total: 0, page: 1, pages: 1 });
    } finally {
      setHistLoading(false);
    }
    // eslint-disable-next-line
  }, [histPage, histSearch]);

  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  // ── Archive index state ─────────────────────────────────────────────
  // The search reads only from the index, so a year has to be scanned once
  // before any job in it can be found.
  const [idx, setIdx] = useState(null);
  const [idxLoading, setIdxLoading] = useState(false);
  const [idxError, setIdxError] = useState('');
  const [scanning, setScanning] = useState(null); // year being scanned

  const loadIndex = useCallback(async () => {
    try {
      const res = await api.get('/repeat-orders/index/status', authHeader);
      setIdx(res.data);
      setScanning(res.data?.running?.year ?? null);
      return res.data;
    } catch (err) {
      setIdxError(err.response?.data?.error || 'Could not load the index status.');
      return null;
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (tab !== 'index') return undefined;
    setIdxLoading(true);
    loadIndex().finally(() => setIdxLoading(false));
    return undefined;
  }, [tab, loadIndex]);

  // Poll only while a scan is actually running — a full year over CIFS takes
  // minutes, and the user needs to see it progressing.
  useEffect(() => {
    if (tab !== 'index' || !idx?.running) return undefined;
    const t = setInterval(loadIndex, 3000);
    return () => clearInterval(t);
  }, [tab, idx?.running, loadIndex]);

  // Load the index status on the Find tab too, so the Year list can show which
  // years actually hold data — searching an unscanned year only ever fails.
  useEffect(() => {
    if (tab !== 'find' || idx) return;
    loadIndex();
  }, [tab, idx, loadIndex]);

  const indexedByYear = {};
  (idx?.years || []).forEach((r) => { indexedByYear[r.year] = r.zip_count || 0; });
  const yearOptions = YEARS.map((y) => ({
    value: y,
    label: String(y),
    hint: indexedByYear[y] ? `${indexedByYear[y].toLocaleString()} zips` : 'not indexed',
  }));

  const rescan = async (y) => {
    setIdxError('');
    setScanning(y);
    try {
      await api.post('/repeat-orders/index/rescan', { year: y }, authHeader);
      await loadIndex();
    } catch (err) {
      setIdxError(err.response?.data?.error || 'Could not start the scan.');
      setScanning(null);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <RefreshCw className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Repeat Orders</h1>
          <p className="text-sm text-gray-500">Find an old job's archived files, extract them, and keep only what you need.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <button
          onClick={() => setTab('find')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${tab === 'find' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <Search className="w-4 h-4" /> Find / Extract
        </button>
        <button
          onClick={() => { setTab('history'); setHistPage(1); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${tab === 'history' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <HistoryIcon className="w-4 h-4" /> History
        </button>
        <button
          onClick={() => setTab('index')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${tab === 'index' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <Database className="w-4 h-4" /> Archive Index
        </button>
      </div>

      {tab === 'find' && (
        <>
          {/* Search form */}
          <form onSubmit={handleFind} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Active Job <span className="text-red-500">*</span></label>
                <input type="text" value={activeJob} onChange={(e) => setActiveJob(e.target.value)}
                  placeholder="Current job no." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Old Job Number <span className="text-red-500">*</span></label>
                <input type="text" value={oldJob} onChange={(e) => setOldJob(e.target.value)}
                  placeholder="e.g. 190513957" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </div>
              <FilterableSelect
                label="Year"
                value={year}
                onChange={setYear}
                placeholder="Type a year…"
                options={yearOptions}
              />
              <FilterableSelect
                label="Month"
                value={month}
                onChange={setMonth}
                placeholder="Type a month…"
                options={MONTHS.map((m) => ({ value: m.v, label: m.label }))}
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button type="submit" disabled={finding}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-medium">
                {finding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {finding ? 'Searching…' : 'Find'}
              </button>
              <span className="text-xs text-gray-400">The archive drive holding the selected year is found automatically.</span>
            </div>
          </form>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {result && !done && result.note && (
            <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{result.note} Please check this is the job you wanted before proceeding.</span>
            </div>
          )}

          {result && !done && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-gray-800">
                  <FileArchive className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold">{result.zip_name}</span>
                  <span className="text-sm text-gray-400">· {(result.files || []).length} files</span>
                </div>
                <button onClick={toggleAll} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  {allSelected ? <Square className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-[420px] overflow-auto">
                {(result.files || []).map((f) => (
                  <label key={f.path} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={!!selected[f.path]} onChange={() => toggle(f.path)} className="w-4 h-4 text-blue-600 rounded" />
                    <span className="flex-1 text-sm text-gray-700 break-all">{f.path}</span>
                    <span className="text-xs text-gray-400">{formatSize(f.size)}</span>
                  </label>
                ))}
                {(result.files || []).length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-gray-400">The archive is empty.</div>
                )}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  {selectedCount} selected · {(result.files || []).length - selectedCount} will be deleted
                </span>
                <button onClick={handleProceed} disabled={proceeding}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-medium">
                  {proceeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {proceeding ? 'Applying…' : 'Proceed'}
                </button>
              </div>
            </div>
          )}

          {done && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 text-emerald-700 mb-3">
                <CheckCircle className="w-6 h-6" />
                <span className="font-semibold text-lg">Done</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Kept {done.kept.length} file(s); deleted {done.deleted_count}. Files moved to the working folder and logged in History.
              </p>
              {done.kept.length > 0 && (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-auto mb-4">
                  {done.kept.map((p) => (<div key={p} className="px-3 py-2 text-sm text-gray-700 break-all">{p}</div>))}
                </div>
              )}
              <button onClick={() => { resetOutput(); setOldJob(''); setActiveJob(''); }}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium">
                <Search className="w-4 h-4" /> New search
              </button>
            </div>
          )}
        </>
      )}

      {tab === 'history' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <form
              onSubmit={(e) => { e.preventDefault(); setHistPage(1); setHistSearch(histQuery.trim()); }}
              className="flex items-center gap-2"
            >
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input type="text" value={histQuery} onChange={(e) => setHistQuery(e.target.value)}
                  placeholder="Search job no., zip, user…"
                  className="border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </div>
              <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm">Search</button>
              {histSearch && (
                <button type="button" onClick={() => { setHistQuery(''); setHistSearch(''); setHistPage(1); }}
                  className="text-sm text-gray-500 hover:text-gray-700">Clear</button>
              )}
            </form>
            <span className="text-sm text-gray-400">{hist.total} record(s)</span>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Active Job</th>
                  <th className="text-left px-3 py-2 font-medium">Old Job</th>
                  <th className="text-left px-3 py-2 font-medium">Period</th>
                  <th className="text-left px-3 py-2 font-medium">Zip</th>
                  <th className="text-center px-3 py-2 font-medium">Required</th>
                  <th className="text-center px-3 py-2 font-medium">Removed</th>
                  <th className="text-left px-3 py-2 font-medium">User</th>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {histLoading && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                )}
                {!histLoading && hist.items.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">No repeat orders yet.</td></tr>
                )}
                {!histLoading && hist.items.map((it) => (
                  <React.Fragment key={it.id}>
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded(expanded === it.id ? null : it.id)}>
                      <td className="px-3 py-2 font-medium text-gray-800">{it.active_job}</td>
                      <td className="px-3 py-2 text-gray-600">{it.old_job}</td>
                      <td className="px-3 py-2 text-gray-600">{it.year ? `${MONTH_LABEL(it.month)} ${it.year}` : '—'}</td>
                      <td className="px-3 py-2 text-gray-600 break-all max-w-[220px] truncate" title={it.zip_name}>{it.zip_name || '—'}</td>
                      <td className="px-3 py-2 text-center"><span className="inline-block bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5 text-xs">{it.required_count}</span></td>
                      <td className="px-3 py-2 text-center"><span className="inline-block bg-red-50 text-red-600 rounded-full px-2 py-0.5 text-xs">{it.non_required_count}</span></td>
                      <td className="px-3 py-2 text-gray-600">{it.user_name}</td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatDate(it.created_at)}</td>
                    </tr>
                    {expanded === it.id && (
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="px-4 py-3">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs font-semibold text-emerald-700 mb-1">Required ({it.required_count})</div>
                              <div className="border border-gray-200 rounded bg-white max-h-40 overflow-auto">
                                {(it.required_files || []).map((p) => <div key={p} className="px-2 py-1 text-xs text-gray-700 break-all border-b border-gray-100 last:border-0">{p}</div>)}
                                {(it.required_files || []).length === 0 && <div className="px-2 py-2 text-xs text-gray-400">None</div>}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-red-600 mb-1">Removed ({it.non_required_count})</div>
                              <div className="border border-gray-200 rounded bg-white max-h-40 overflow-auto">
                                {(it.non_required_files || []).map((p) => <div key={p} className="px-2 py-1 text-xs text-gray-500 break-all border-b border-gray-100 last:border-0">{p}</div>)}
                                {(it.non_required_files || []).length === 0 && <div className="px-2 py-2 text-xs text-gray-400">None</div>}
                              </div>
                            </div>
                          </div>
                          {it.folder && <div className="text-xs text-gray-400 mt-2 break-all">Folder: {it.folder}</div>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {hist.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-gray-500">Page {hist.page} of {hist.pages}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setHistPage((p) => Math.max(1, p - 1))} disabled={hist.page <= 1}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50">
                  <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                <button onClick={() => setHistPage((p) => Math.min(hist.pages, p + 1))} disabled={hist.page >= hist.pages}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'index' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="mb-4">
            <h2 className="font-semibold text-gray-800">Archive Index</h2>
            <p className="text-sm text-gray-500 mt-1">
              Searches read from this index, not the file server. A year has to be scanned
              once before its jobs can be found — and re-scanned after new jobs are archived.
            </p>
          </div>

          {idxError && (
            <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{idxError}</span>
            </div>
          )}

          {idx?.running && (
            <div className="mb-4 flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-3 text-sm">
              <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
              <span>
                Scanning {idx.running.year} — {idx.running.indexed} ZIPs indexed so far.
                This runs in the background; you can leave this page.
              </span>
            </div>
          )}

          {idxLoading && !idx ? (
            <div className="text-gray-500 text-sm py-6">Loading index status…</div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Year</th>
                    <th className="text-left px-3 py-2 font-medium">Mounted</th>
                    <th className="text-right px-3 py-2 font-medium">ZIPs indexed</th>
                    <th className="text-left px-3 py-2 font-medium">Last scan</th>
                    <th className="text-right px-3 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(idx?.years || []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                        No archive years are mounted yet.
                      </td>
                    </tr>
                  )}
                  {(idx?.years || []).map((r) => (
                    <tr key={r.year} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-800">{r.year}</td>
                      <td className="px-3 py-2">
                        {r.mounted ? (
                          <span className="text-green-700" title={`${r.entries} item(s) in ${r.year_dir}`}>Yes</span>
                        ) : r.year_dir && r.readable === false ? (
                          <span className="text-red-600 font-medium"
                            title={`${r.year_dir} exists but cannot be read — permissions or a stale mount`}>
                            Unreadable
                          </span>
                        ) : r.year_dir ? (
                          <span className="text-amber-600 font-medium"
                            title={`${r.year_dir} exists but is empty — the share is probably not mounted`}>
                            Empty
                          </span>
                        ) : (
                          <span className="text-gray-400" title="No folder for this year on the archive drive">Not mounted</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {r.zip_count ? r.zip_count.toLocaleString() : <span className="text-amber-600">Never scanned</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {r.last_scan ? (
                          <>
                            {formatDate(r.last_scan)}
                            {r.last_by && <span className="text-gray-400"> · by {r.last_by}</span>}
                            {r.last_status === 'failed' && <span className="text-red-600"> · failed</span>}
                            {r.last_errors?.length > 0 && (
                              <div className="text-xs text-red-600 mt-0.5">{r.last_errors[0]}</div>
                            )}
                            {r.nonstandard_months?.length > 0 && (
                              <div className="text-xs text-amber-700 mt-0.5">
                                Off-standard month folder{r.nonstandard_months.length > 1 ? 's' : ''}:{' '}
                                {r.nonstandard_months.join(', ')}
                              </div>
                            )}
                            {r.ignored_folders?.length > 0 && (
                              <div className="text-xs text-gray-500 mt-0.5" title="These folders were skipped — no jobs indexed from them">
                                Skipped: {r.ignored_folders.join(', ')}
                              </div>
                            )}
                          </>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {idx?.can_scan ? (
                          <button
                            onClick={() => rescan(r.year)}
                            disabled={!!idx?.running || !r.mounted}
                            title={!r.mounted ? 'This year is not mounted on the archive drive' : ''}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {scanning === r.year && idx?.running
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <RefreshCw className="w-4 h-4" />}
                            {r.zip_count ? 'Rescan' : 'Scan'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Admin only</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {idx && (
            <p className="text-xs text-gray-500 mt-3">
              {idx.total?.toLocaleString() || 0} archived ZIPs indexed in total.
              Re-scan a year after archiving new jobs, otherwise they will not be findable.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default RepeatOrders;
